import React, { useState, useEffect } from 'react';
import { apiClient } from '../App';
import Layout from '../components/Layout';
import MonthlySummaryModal from '../components/MonthlySummaryModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  BarChart3, Calendar, Trophy, TrendingUp, ChevronRight, 
  Loader2, FileText
} from 'lucide-react';
import { hikersHeroBgStyle } from '../constants/backgrounds';

const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month - 1];
};

const MySummaries = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      const res = await apiClient.get('/summaries/history');
      setSummaries(res.data);
    } catch (err) {
      console.error('Failed to fetch summaries:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewSummary = async (year, month) => {
    try {
      const res = await apiClient.get(`/summaries/${year}/${month}`);
      setSelectedSummary(res.data);
      setModalOpen(true);
    } catch (err) {
      toast.error('Failed to load summary');
    }
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-[#B8871E]';
    return 'text-stone-500';
  };

  const getPerformanceBg = (percentage) => {
    if (percentage >= 80) return 'bg-green-50 border-green-200';
    if (percentage >= 50) return 'bg-[#EFB342]/10 border-[#EFB342]/30';
    return 'bg-stone-50 border-stone-200';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="animate-spin text-[#EFB342]" size={40} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={hikersHeroBgStyle} className="min-h-screen" data-testid="my-summaries-page">
        <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 bg-[#EFB342] rounded-2xl flex items-center justify-center">
            <BarChart3 className="text-black" size={28} />
          </div>
          <div>
            <h1 className="font-heading text-3xl text-stone-900">My Monthly Summaries</h1>
            <p className="text-stone-600">Review your progress month by month</p>
          </div>
        </div>

        {/* Summaries List */}
        {summaries.length > 0 ? (
          <div className="space-y-4">
            {summaries.map((summary) => (
              <div
                key={`${summary.year}-${summary.month}`}
                className={`border rounded-2xl p-6 transition-all hover:border-[#EFB342]/50 cursor-pointer ${getPerformanceBg(summary.overall_percentage)}`}
                onClick={() => viewSummary(summary.year, summary.month)}
                data-testid={`summary-${summary.year}-${summary.month}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center">
                      <Calendar className="text-[#EFB342]" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-stone-900">
                        {getMonthName(summary.month)} {summary.year}
                      </h3>
                      <p className="text-stone-600 text-sm">
                        {summary.days_checked_in} days checked in • {summary.badges_earned_this_month} badges
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getPerformanceColor(summary.overall_percentage)}`}>
                        {summary.total_points}/{summary.max_possible_points}
                      </div>
                      <div className="text-sm text-stone-600">{summary.overall_percentage}%</div>
                    </div>
                    <ChevronRight className="text-stone-500" size={24} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
            <FileText className="mx-auto text-stone-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No Summaries Yet</h3>
            <p className="text-stone-600 max-w-md mx-auto">
              Your monthly summaries will appear here after each 28-day challenge ends. 
              Keep checking in daily to build your progress!
            </p>
          </div>
        )}

        {/* Summary Modal */}
        <MonthlySummaryModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          summary={selectedSummary}
        />
      </div>
      </div>
    </Layout>
  );
};

export default MySummaries;
